import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { AuthService, IdentityUser, PagedUsers } from 'app/common/services/auth';
import { Router } from '@angular/router';

interface RoleOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-identity-dashboard',
  standalone: true, // remove this line (and `imports`) if your app uses NgModules
  imports: [CommonModule],
  templateUrl: './identity-dashboard.html',
  styleUrls: ['./identity-dashboard.scss'],
})
export class IdentityDashboardComponent implements OnInit, OnDestroy {
  private readonly identityService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);

  // Same toast options as the login page: bottom center, 3 seconds
  private readonly toastOptions = {
    positionClass: 'toast-bottom-center',
    timeOut: 3000,
  };

  readonly pageSizeOptions = [10, 12, 24, 48];

  pageNumber = 1; // 1-based, as the API expects
  pageSize = 10;
  query = '';

  loading = false;
  errorMessage: string | null = null;

  visibleUsers: IdentityUser[] = [];
  total: number | null = null;

  /* ---------- Selection + bulk role assignment ---------- */

  /** Selected users, keyed by id. Kept across pages and searches until cleared. */
  private readonly selected = new Map<string, IdentityUser>();

  roles: RoleOption[] = [];
  rolesLoading = false;
  rolesError: string | null = null;
  selectedRoleName = '';
  assigning = false;

  private readonly search$ = new Subject<string>();
  private readonly load$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  /* ---------- Toasts (same style as the login page) ---------- */

  /** Shows a bottom-center toast. If no title is given, a default one is used. */
  private toast(kind: 'success' | 'error' | 'warning', message: string, title?: string): void {
    const defaultTitle = { success: 'Success', error: 'Error', warning: 'Warning' }[kind];
    this.toastr[kind](message, title ?? defaultTitle, this.toastOptions);
  }

  ngOnInit(): void {
    // A success message passed from another page (e.g. after registering a user).
    const successMessage =
      sessionStorage.getItem('identity-success-message') ??
      (history.state as { successMessage?: string } | undefined)?.successMessage ??
      (this.router.getCurrentNavigation()?.extras?.state as { successMessage?: string } | undefined)?.successMessage;

    if (successMessage) {
      sessionStorage.removeItem('identity-success-message');
      setTimeout(() => this.toast('success', successMessage, 'Success'), 50);
    }

    this.loadRoles();

    // Every load request cancels the previous in-flight one (switchMap).
    this.load$
      .pipe(
        tap(() => {
          this.loading = true;
          this.errorMessage = null;
        }),
        switchMap(() =>
          this.identityService.getAllUsers(this.pageNumber, this.pageSize, this.query).pipe(
            catchError((err: HttpErrorResponse) => {
              this.errorMessage =
                err.status === 401 || err.status === 403
                  ? "You don't have permission to view users."
                  : 'Could not load users. Please try again.';
              this.toast('error', this.errorMessage, 'Load failed');
              return of<PagedUsers | null>(null);
            }),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        this.loading = false;
        this.visibleUsers = result?.items ?? [];
        this.total = result?.total ?? (result ? null : 0);
      });

    // Wait for the user to stop typing before hitting the API.
    this.search$
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.query = value;
        this.pageNumber = 1;
        this.load$.next();
      });

    this.load$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ---------- Derived state for the template ---------- */
  get rangeStart(): number {
    return this.visibleUsers.length ? (this.pageNumber - 1) * this.pageSize + 1 : 0;
  }

  get rangeEnd(): number {
    return this.visibleUsers.length ? this.rangeStart + this.visibleUsers.length - 1 : 0;
  }

  get rangeLabel(): string {
    if (!this.visibleUsers.length) return '0 results';
    const range = `${this.rangeStart}-${this.rangeEnd}`;
    return this.total !== null ? `${range} of ${this.total}` : range;
  }

  get hasPrev(): boolean {
    return this.pageNumber > 1;
  }

  get hasNext(): boolean {
    // Without a total from the API, assume there's more when the page came back full.
    return this.total !== null
      ? this.rangeEnd < this.total
      : this.visibleUsers.length === this.pageSize;
  }

  fullName(user: IdentityUser): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.userName;
  }

  /* ---------- Events ---------- */
  loadUsers(): void {
    this.load$.next();
  }

  onSearch(value: string): void {
    this.search$.next(value);
  }

  onPageSizeChange(value: string): void {
    this.pageSize = Number(value);
    this.pageNumber = 1;
    this.load$.next();
  }

  prevPage(): void {
    if (!this.hasPrev) return;
    this.pageNumber--;
    this.load$.next();
  }

  nextPage(): void {
    if (!this.hasNext) return;
    this.pageNumber++;
    this.load$.next();
  }

  onAddUser(): void {
    this.router.navigate(['/identity/identity-register']);
  }

  onEdit(user: IdentityUser): void {
    this.router.navigate(['/identity/identity-preview', user.id], { state: { user } });
  }

  trackById(_: number, user: IdentityUser): string {
    return user.id;
  }

  /* ---------- Selection ---------- */

  get selectedCount(): number {
    return this.selected.size;
  }

  isSelected(user: IdentityUser): boolean {
    return this.selected.has(user.id);
  }

  toggleUser(user: IdentityUser): void {
    if (this.selected.has(user.id)) {
      this.selected.delete(user.id);
    } else {
      this.selected.set(user.id, user);
    }
  }

  get allOnPageSelected(): boolean {
    return this.visibleUsers.length > 0 && this.visibleUsers.every((u) => this.selected.has(u.id));
  }

  get someOnPageSelected(): boolean {
    return !this.allOnPageSelected && this.visibleUsers.some((u) => this.selected.has(u.id));
  }

  toggleAllOnPage(checked: boolean): void {
    for (const user of this.visibleUsers) {
      if (checked) this.selected.set(user.id, user);
      else this.selected.delete(user.id);
    }
  }

  clearSelection(): void {
    this.selected.clear();
  }

  /* ---------- Roles ---------- */

  loadRoles(): void {
    this.rolesLoading = true;
    this.rolesError = null;

    this.identityService.getRoles().subscribe({
      next: (res) => {
        this.rolesLoading = false;
        this.roles = this.normalizeRoles(res);
      },
      error: () => {
        this.rolesLoading = false;
        this.rolesError = 'Could not load roles.';
        this.toast('error', this.rolesError, 'Load failed');
      },
    });
  }

  private normalizeRoles(res: any): RoleOption[] {
    const list = Array.isArray(res) ? res : (res?.data ?? res?.roles ?? res?.items ?? []);
    if (!Array.isArray(list)) return [];

    return list.map((role: any) => {
      if (typeof role === 'string') return { id: role, name: role };
      const id = role.id ?? role.roleId ?? role.value ?? role.code ?? role.name;
      const name = role.name ?? role.roleName ?? role.label ?? role.description ?? String(id);
      return { id: String(id), name: String(name) };
    });
  }

  /** Turns a user's current roles (names or ids) into role names. */
  private currentRoleNames(user: IdentityUser): string[] {
    const raw = Array.isArray(user.roles) ? user.roles.map((r) => String(r).trim()).filter(Boolean) : [];

    return raw.map((value) => {
      const key = value.toLowerCase();
      const match = this.roles.find((r) => r.id.toLowerCase() === key || r.name.toLowerCase() === key);
      return match?.name ?? value;
    });
  }

  assignRoleToSelected(): void {
    if (this.assigning) return;

    if (this.selected.size === 0) {
      this.toast('warning', 'Select at least one user first.', 'No user selected');
      return;
    }

    if (!this.selectedRoleName) {
      this.toast('warning', 'Choose a role to add.', 'No role selected');
      return;
    }

    const roleName = this.selectedRoleName;
    const roleKey = roleName.toLowerCase();

    const users = [...this.selected.values()];
    const alreadyHave = users.filter((u) =>
      this.currentRoleNames(u).some((r) => r.toLowerCase() === roleKey),
    );
    const targets = users.filter((u) => !alreadyHave.includes(u));

    if (!targets.length) {
      const message =
        alreadyHave.length === 1
          ? `${this.fullName(alreadyHave[0])} already has the "${roleName}" role.`
          : `All ${alreadyHave.length} selected users already have the "${roleName}" role.`;

      this.toast('warning', message, 'Already assigned');

      // They need no change, so take them out of the selection.
      alreadyHave.forEach((u) => this.selected.delete(u.id));
      return;
    }

    this.assigning = true;

    // One request per user, run in parallel. A failure on one user doesn't cancel the others.
    // Each request sends the user's existing roles plus the new one, so nothing is overwritten.
    const requests = targets.map((user) => {
      const roles = [...new Set([...this.currentRoleNames(user), roleName])];

      return this.identityService.updateUserRoles(user.userName, roles).pipe(
        map((res: any) => {
          const failed = res?.succeeded === false || res?.Succeeded === false;
          return { user, error: failed ? (res?.message ?? res?.Message ?? 'Failed') : null };
        }),
        catchError((err: HttpErrorResponse) =>
          of({ user, error: this.errorMessageFrom(err) }),
        ),
      );
    });

    forkJoin(requests)
      .pipe(finalize(() => (this.assigning = false)))
      .subscribe((results) => {
        const succeeded = results.filter((r) => !r.error);
        const failed = results.filter((r) => r.error);

        // Remove the users that succeeded from the selection; failed ones stay for retry.
        succeeded.forEach((r) => this.selected.delete(r.user.id));

        if (succeeded.length) {
          const message = this.buildSuccessMessage(
            roleName,
            succeeded.map((r) => this.fullName(r.user)),
            alreadyHave.length,
          );

          this.toast('success', message, 'Role added');
          this.load$.next(); // refresh so the rows show the new roles
        }

        if (failed.length) {
          this.toast(
            'error',
            `Could not update ${failed.length} user(s): ` +
              failed.map((f) => `${f.user.userName} (${f.error})`).join('; '),
            'Role not added',
          );
        }
      });
  }

  /** e.g. `"Admin" role added to John Doe.` or `"Admin" role added to 5 users: A, B, C and 2 more.` */
  private buildSuccessMessage(roleName: string, names: string[], alreadyHaveCount: number): string {
    const MAX_NAMES = 3;

    const who =
      names.length === 1
        ? names[0]
        : names.length <= MAX_NAMES
          ? `${names.length} users: ${names.join(', ')}`
          : `${names.length} users: ${names.slice(0, MAX_NAMES).join(', ')} and ${names.length - MAX_NAMES} more`;

    const skipped = alreadyHaveCount
      ? ` ${alreadyHaveCount} selected user(s) already had this role.`
      : '';

    return `"${roleName}" role added to ${who}.${skipped}`;
  }

  private errorMessageFrom(err: HttpErrorResponse): string {
    const body = err.error;
    if (typeof body === 'string' && body.trim()) return body;
    const message = body?.message ?? body?.Message;
    if (message) return message;
    if (err.status === 401 || err.status === 403) return 'No permission';
    return 'Request failed';
  }
}